import React from 'react';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { StarIcon, EnvelopeIcon, PhoneIcon, IdentificationIcon } from '@heroicons/react/24/outline';
import { PhoneNumberTags } from '@medflex/shared-types';
import { trackCustomEvent } from '@medflex/utils/Tracking';
import UserInfo from '../UserInfo';
import CopyButton from '../CopyButton/CopyButton';
import Link from '../Link';
import { showSuccessToast } from '../Toast';
import Button from '../Button';

const ContactInfo = ({
  fullNameWithTitleAndSalutation,
  initials,
  workplace,
  workArea,
  areaOfActivity,
  organization,
  country,
  city,
  dateOfBirth,
  avatar,
  email,
  phoneNumbers,
  contactPageLink,
  pvsId,
  handleOpenInPVS,
  hideDoB,
  hideHeader,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'ContactInfo' });

  const handleEmailTracking = () => {
    trackCustomEvent({
      eventCategory: 'user interactions',
      eventAction: 'ContactInfo | link | mailto',
      eventValue: 'chat | AddressBook',
    });
  };

  return (
    <>
      {!hideHeader && (
        <>
          <UserInfo
            className="font-semibold"
            title={fullNameWithTitleAndSalutation}
            avatarInitials={initials}
            role={workplace}
            subtitle={workArea || areaOfActivity}
            avatarSrc={avatar}
          />
          {handleOpenInPVS && (
            <Button className="font-semibold" kind="link" onClick={() => handleOpenInPVS(pvsId)}>
              <IdentificationIcon className="w-6 h-6" />
              <span className="ml-2">{t('pvsId')}</span>
            </Button>
          )}
          {(organization || country || city || dateOfBirth) && <hr />}
          {organization && <div className="truncate">{organization}</div>}
        </>
      )}
      {(country || city) && (
        <div className="truncate">
          {city && `${city}, `}
          {country}
        </div>
      )}
      {dateOfBirth && !hideDoB && (
        <div className="flex flex-row truncate">
          <StarIcon className="text-slate-600 w-6" />
          <span className="ml-1">{dateOfBirth.split('-').reverse().join('.')}</span>
        </div>
      )}
      {email && (
        <>
          <hr />
          <div className="flex space-x-2">
            <div className="flex flex-row truncate">
              <EnvelopeIcon className="text-slate-600 w-6" />
              <Link
                className="ml-2"
                href={`mailto:${email}`}
                target="_blank"
                rel="noreferrer"
                onClick={handleEmailTracking}
              >
                {email}
              </Link>
            </div>
            <CopyButton
              value={email}
              onClick={() =>
                showSuccessToast(t('emailCopied'), null, {
                  toastId: 'contactDetailsCopied',
                })
              }
            />
          </div>
        </>
      )}
      {phoneNumbers.length
        ? phoneNumbers.map(({ tag, number }) => (
            <div className="flex flex-row truncate" key={`${tag}${number}`}>
              <div className="text-slate-600 flex flex-row">
                <PhoneIcon className=" w-6" />
                <span className="ml-2 w-16">{t(`phoneTags.${tag}`)}</span>
              </div>
              <span className="ml-1">{number}</span>
              <CopyButton
                value={number}
                onClick={() =>
                  showSuccessToast(t('phoneNumberCopied'), null, {
                    toastId: 'contactDetailsCopied',
                  })
                }
              />
            </div>
          ))
        : null}
      {contactPageLink && (
        <div className="flex space-x-2">
          <Link rel="noreferrer" href={contactPageLink} target="_blank">
            {t('contactPageLinkTitle')}
          </Link>
          <CopyButton
            value={contactPageLink}
            onClick={() =>
              showSuccessToast(t('contactPageLinkCopied'), null, {
                toastId: 'contactDetailsCopied',
              })
            }
          />
        </div>
      )}
    </>
  );
};

ContactInfo.defaultProps = {
  avatar: null,
  dateOfBirth: null,
  organization: null,
  workArea: null,
  workplace: null,
  areaOfActivity: null,
  email: null,
  phoneNumbers: [],
  city: null,
  country: null,
  initials: null,
  contactPageLink: null,
  pvsId: null,
  handleOpenInPVS: null,
  hideDoB: false,
  hideHeader: false,
};

ContactInfo.propTypes = {
  avatar: PropTypes.string,
  dateOfBirth: PropTypes.string,
  organization: PropTypes.string,
  workArea: PropTypes.string,
  workplace: PropTypes.string,
  areaOfActivity: PropTypes.string,
  email: PropTypes.string,
  phoneNumbers: PropTypes.arrayOf(
    PropTypes.shape({
      tag: PropTypes.oneOf(Object.values(PhoneNumberTags)),
      number: PropTypes.string,
    }),
  ),
  city: PropTypes.string,
  country: PropTypes.string,
  fullNameWithTitleAndSalutation: PropTypes.string.isRequired,
  contactPageLink: PropTypes.string,
  initials: PropTypes.string,
  pvsId: PropTypes.string,
  handleOpenInPVS: PropTypes.func,
  hideDoB: PropTypes.bool,
  hideHeader: PropTypes.bool,
};

export default ContactInfo;
